<script>
import {FormButton, ToggleButton, URLField} from '@/components/form';
import ModalDialog from '@/components/ModalDialog.vue';
import { GlobalPolicy } from '@/policies/global';
import SelectField from '../form/SelectField.vue';
import PaginationTable from '@/components/PaginationTable.vue';
import TextInput from '../form/TextInput.vue';
import CollectionAmenity from '@/models/amenities/collection';
import CommunityAmenity from '@/models/amenities/community';
import axios from 'axios';
import AmenitiesType from '@/enums/AmenitiesType';
import AmenitiesGroup from '@/enums/AmenitiesGroup';
import { FontAwesomeController } from '@/controllers/font_awesome';

export default {
    setup() {
        return {
            GlobalPolicy,
            AmenitiesGroup,
            AmenitiesType
        }
    },
    data() {
        return {
            modal: false,
            index: false,
            amenity: null,
            deleting: false,
            attachments: [],
            icons: []
        }
    },
    emits: [
        'update'
    ],
    computed: {
        columns() {
            let cols = [
                {
                    name: 'position',
                    label: 'Position',
                    sort: false,
                    default: true
                },
                {
                    name: 'icon',
                    label: 'Icon',
                    sort: false,
                    default: true
                },
                {
                    name: 'title',
                    label: 'Title',
                    sort: false,
                    default: true
                },
                {
                    name: 'group',
                    label: 'Group',
                    sort: false,
                    default: true
                },
                {
                    name: 'type',
                    label: 'Type',
                    sort: false,
                    default: true
                }
            ];
            if (GlobalPolicy.edit()) {
                cols.push({
                    name: 'actions',
                    label: '',
                    sort: false,
                    default: true,
                    class: 'text-right'
                });
            }
            return cols;
        },
        amenityTypes() {
            return AmenitiesType.levels.filter(type => this.amenity.types.includes(type.value))
        }
    },
    methods: {
        move(amenity, direction) {
            const item = this.item;
            let index = item.meta.amenities.indexOf(amenity),
                to = direction == 'up' ? index - 1 : index + 2,
                from = direction == 'up' ? index + 1 : index;
            item.meta.amenities.splice(to, 0, amenity)
            item.meta.amenities.splice(from, 1);
            this.$emit('update', item);
        },
        validate() {
            if(this.amenity.title == '' || this.amenity.url == '') {
                console.error('Missing required values');
                return false;
            }
            return true;
        },
        edit(index) {
            this.modal = 1;
            this.index = index;
            this.amenity = this.item.meta.amenities[index];
        },
        save() {
            if(!this.validate()) return;
            const item = this.item;
            if (this.index === false) {
                item.meta.amenities.push(this.amenity);
            } else {
                item.meta.amenities.splice(this.index, 1, this.amenity);
            }
            this.$emit('update', item);
            this.cancel();
        },
        drop() {
            if(this.index === false) {
                console.error('You must select an item to delete');
                return;
            }
            const item = this.item;
            item.meta.amenities.splice(this.index, 1);
            this.$emit('update', item);
            this.cancel();
        },
        openDelete(i) {
            this.index = i;
            this.deleting = true;
        },
        cancel() {
            this.initialize();
            this.index = false;
            this.modal = false;
            this.deleting = false;
        },
        initialize() {
            switch(this.context) {
                case 'collection' :
                    this.amenity = new CollectionAmenity;
                    break;
                case 'division' :
                    this.amenity = new CommunityAmenity;
                    break;
            }
        }        
    },
    props: {
        item: {
            type: Object,
            required: true
        },
        context: {
            type: String,
            required: true,
        }
    },
    components: {
        FormButton, ModalDialog, URLField, SelectField, PaginationTable, TextInput, ToggleButton
    },
    async created() {
        this.initialize();
        axios.get(`${this.context}s/${this.item.id}/attachments`).then(resp => {
            this.attachments = resp.data;
        });
        this.icons = await FontAwesomeController.getIcons().catch(error => {
            console.error('FontAwesome getIcons failed', error?.response?.data ?? error);
            return [];
        });
    },
    watch: {
        'amenity.media': {
            handler(next,prev) {
                if(!prev) return;
                this.amenity.media_item = null;
            }
        }
    }
}
</script>

<template>
    <div class="flex flex-col lg:flex-row justify-between items-center">
        <h2 class="text-xl leading-9 font-headline font-medium text-bhav-blue-600">Amenities</h2>
        <FormButton v-if="GlobalPolicy.edit()" color="blue" type="button" @click="() => {
            modal = true
        }">
            <span class="fak fa-plus mr-1"></span>Add
        </FormButton>
    </div>
    <PaginationTable empty-message="There are currently no amenities." :data="item.meta.amenities" :columns="columns" :view="`${$route.name}-${$route.params.tab}`" :legacy="false">
        <template #col-position="{row}">
            <div class="flex gap-2 items-center py-4">
                <div class="text-sm leading-5 text-oakwood-gray-900 mr-2">{{ item.meta.amenities.indexOf(row) + 1 }}
                </div>
                <button type="button" :disabled="item.meta.amenities.indexOf(row) == 0" @click="move(row, 'up')"
                    :class="{ 'cursor-pointer': item.meta.amenities.indexOf(row) > 0, 'text-oakwood-gray-300 cursor-not-allowed': item.meta.amenities.indexOf(row) == 0 }"><span
                        class="far fa-arrow-up"></span></button>
                <button type="button" :disabled="item.meta.amenities.indexOf(row) == item.meta.amenities.length - 1"
                    @click="move(row, 'down')"
                    :class="item.meta.amenities.indexOf(row) < item.meta.amenities.length - 1 ? 'cursor-pointer' : 'text-oakwood-gray-300 cursor-not-allowed'"><span
                        class="far fa-arrow-down"></span></button>
            </div>
        </template>

        <template #col-icon="{row}">
            <button type="button" @click="edit(item.meta.amenities.indexOf(row))" class="block py-4 text-bhav-blue-500 hover:text-bhav-blue-700">
                <span :class="row.icon"></span>
            </button>
        </template>

        <template #col-title="{row}">
            <button type="button" @click="edit(item.meta.amenities.indexOf(row))" class="block py-4 text-bhav-blue-500 hover:text-bhav-blue-700">
                {{ row.title }}
            </button>
        </template>

        <template #col-group="{row}">
            <button type="button" @click="edit(item.meta.amenities.indexOf(row))" class="block py-4 text-bhav-blue-500 hover:text-bhav-blue-700">
                {{ AmenitiesGroup.label(row.group) }}
            </button>
        </template>

        <template #col-type="{row}">
            <button type="button" @click="edit(item.meta.amenities.indexOf(row))" class="block py-4 text-bhav-blue-500 hover:text-bhav-blue-700">
                {{ AmenitiesType.label(row.type) }}
            </button>
        </template>

        <template #col-actions="{row}" v-if="GlobalPolicy.edit()">
            <div class="flex gap-2 justify-end py-4">
                <FormButton type="button" @click="edit(item.meta.amenities.indexOf(row))" icon="fa-pen" color="blue" prefix-class="fak">
                    Edit
                </FormButton>
                <FormButton type="button" @click="openDelete(item.meta.amenities.indexOf(row))" prefix-class="fak" icon="fa-trash" color="red"/>
            </div>
        </template>
    </PaginationTable>

    <ModalDialog :open="modal" :no-padding="true" @close="cancel">
        <template #header>
            <h3 class="text-lg leading-6 font-medium text-bhav-blue-500">
                Add Amenity
            </h3>
        </template>
        <template #body>
            <div class="mt-6 sm:mt-5">
                <div
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <label for="title"
                        class="block text-sm font-medium leading-5 text-oakwood-gray-700 sm:mt-px sm:pt-2">
                        Title *
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2">
                        <TextInput required="true" id="title" v-model="amenity.title" class="w-full"/>
                    </div>
                </div>

                <div
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <label for="description"
                        class="block text-sm font-medium leading-5 text-oakwood-gray-700 sm:mt-px sm:pt-2">
                        Description *
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2">
                        <textarea required="true" rows="3" maxlength="250" id="description" v-model="amenity.description" class="w-full bg-bhav-cool-gray-50 rounded-lg text-bhav-blue-500 px-2 py-3 sm:text-sm"/>
                        <p class="text-sm text-bhav-blue-500 text-right" :class="{'text-bhav-red-500': amenity.description.length >= 250}">{{amenity.description.length}}/250 characters</p>
                    </div>
                </div>

                <div
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <p
                        class="block text-sm font-medium leading-5 text-oakwood-gray-700 sm:mt-px sm:pt-2">
                        Icon
                    </p>
                    <div class="mt-1 sm:mt-0 sm:col-span-2 grid grid-cols-12 gap-2">
                        <div v-for="icon, i in icons" class="border border-bhav-blue-50 rounded-lg text-xl hover:text-bhav-blue-500 hover:bg-bhav-blue-50 overflow-hidden">
                            <label :for="`icon-${i}`" :title="icon.name" class="cursor-pointer w-full aspect-square flex items-center justify-center" :class="[amenity.icon === icon.class ? 'text-white bg-bhav-blue-500' : '']"><span :class="icon.class"></span><span class="sr-only">{{ icon.name }}</span></label>
                            <input type="radio" :id="`icon-${i}`" name="icon" v-model="amenity.icon" :value="icon.class" class="sr-only" />
                        </div>
                    </div>
                </div>

                <div v-if="amenity.groups.length"
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <label for="group"
                        class="block text-sm font-medium leading-5 text-oakwood-gray-700 sm:mt-px sm:pt-2"
                        aria-describedby="group_tip">
                        Zonda Group *
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2">
                        <SelectField required="true" id="group" v-model="amenity.group" :options="AmenitiesGroup.levels" :allowNull="false" />
                    </div>
                    <p id="group_tip" class="col-span-full text-sm italic text-bhav-cool-gray-500">
                        Zonda supports two different groups of amenities: <strong>Standard</strong> and <strong>Open</strong>. <strong>Standard</strong> amenities are based on a pre-determined list of avalable types and do not display any other text. <strong>Open</strong> amenities offer more flexibility under a small subset of types that use the Title field to display the amenity in a bulleted list.
                    </p>
                </div>

                <div v-if="amenity.types.length"
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <label for="type"
                        class="block text-sm font-medium leading-5 text-oakwood-gray-700 sm:mt-px sm:pt-2"
                        aria-describedby="type_tip">
                        Zonda Type *
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2">
                        <SelectField required="true" id="type" v-model="amenity.type" :options="amenityTypes" :allowNull="false" />
                    </div>
                    <p id="type_tip" class="col-span-full text-sm italic text-bhav-cool-gray-500">
                        Zonda types are different between <strong>Standard</strong> and <strong>Open</strong> amenity groups. The types for <strong>Standard</strong> amenity groups indicate the presence of the amenity selected in the community. The types for <strong>Open</strong> amenities list the custom text for each amenity under their respective type.
                    </p>
                </div>

                <div
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <p
                        class="block text-sm font-medium leading-5 text-oakwood-gray-700 sm:mt-px sm:pt-2">
                        Media
                    </p>
                    <ul class="mt-1 sm:col-span-2 flex gap-4 text-sm text-oakwood-gray-700 items-center sm:mt-px sm:pt-2">
                        <li class="flex gap-1" v-for="media, i in [{label: 'None', value: null}, {label: 'Video', value: 'video'}, {label: 'Image', value: 'image'}]">
                            <input type="radio" name="media" id="media-none" v-model="amenity.media" :value="media.value">
                            <label for="media-none">{{ media.label }}</label>
                        </li>
                    </ul>
                </div>
                
                <div v-if="amenity.media === 'video'"
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <label for="url"
                        class="block text-sm font-medium leading-5 text-oakwood-gray-700 sm:mt-px sm:pt-2">
                        Video
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2 flex gap-2 flex-col">
                        <iframe class="w-full aspect-video" v-if="amenity.media_item" :src="amenity.media_item"></iframe>
                        <URLField id="url" v-model="amenity.media_item" width="w-full" />
                    </div>
                </div> 

                <div v-if="amenity.media === 'image'"
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <label for="url"
                        class="block text-sm font-medium leading-5 text-oakwood-gray-700 sm:mt-px sm:pt-2">
                        Image
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2 grid grid-cols-3 gap-2">
                        <div v-for="image, i in attachments" class="transition-opacity" :class="[amenity.media_item === image.media.id ? '' : 'opacity-40 hover:opacity-100']">
                            <label :for="`image-${i}`" class="relative cursor-pointer w-full aspect-square flex items-center justify-center rounded-lg overflow-hidden border-2" :class="[amenity.media_item === image.media.id ? 'border-bhav-blue-500' : 'border-transparent']">
                                <img class="object-cover w-full h-full" :src="image.media.url" :alt="image.media.alt" />
                                <div v-if="amenity.media_item === image.media.id" class="absolute inset-0 bg-bhav-blue-500/50 flex items-center justify-center">
                                    <span class="fas fa-check text-white text-2xl drop-shadow"></span>
                                </div>
                            </label>
                            <input type="radio" :id="`image-${i}`" name="image" v-model="amenity.media_item" :value="image.media.id" class="sr-only" />
                        </div>
                    </div>
                </div>
                
                <div
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <label for="url"
                        class="block text-sm font-medium leading-5 text-oakwood-gray-700 sm:mt-px sm:pt-2">
                        URL
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2 flex flex-col items-center gap-2">
                        <URLField id="url" v-model="amenity.url" width="w-full" />
                        <div class="w-full flex justify-end items-center gap-2">
                            <label class="text-blue-500">Open in new tab</label>
                            <ToggleButton id="amenity.open_in_new_tab" @changed="(e) => { amenity.open_in_new_tab = !amenity.open_in_new_tab }" :checked="amenity.open_in_new_tab"
                                label="Open in new tab"/>
                        </div>
                    </div>
                </div>       
            </div>

        </template>

        <template #buttons>
            <div class="flex justify-end mt-5 pt-5">
                <span class="inline-flex rounded-md shadow-sm">
                    <FormButton type="button" @click="cancel"color="gray">
                        Cancel
                    </FormButton>
                </span>
                <span class="ml-3 inline-flex rounded-md shadow-sm">
                    <FormButton type="button" @click="save" color="blue">
                        Save
                    </FormButton>
                </span>
            </div>
        </template>
    </ModalDialog>
    <ModalDialog v-if="deleting" :no-padding="true" :open="deleting" @close="cancel">
        <template #header>
            <h2 class="text-lg text-oakwood-red-600 font-semibold">Confirm Amenity Deletion</h2>
        </template>
        <template #body>
            <p class="text-oakwood-gray-900"><strong>Warning!</strong> You are about to delete <em>{{ item.meta.amenities[index]?.title }}</em>. Are you sure?</p>
        </template>
        <template #buttons>
            <FormButton type="button" @click="cancel" color="gray">
                Cancel
            </FormButton>
            <FormButton type="button" @click="drop" prefix-class="fak" icon="fa-trash" color="red"/>
        </template>
    </ModalDialog>
</template>